import React, { useEffect, useState } from "react";
import { getWorklistTATFilters } from "~/services/supervisor.services";

type CutoffMap = Record<string, number>;

type FilterItem = {
  ColorName: string;
  Description: string;
};

type Props = {
  data?: CutoffMap;
  onFilterChange?: (filter: string) => void;
  activeFilter?: string | null;
};

type ColorStyle = {
  border: string;
  bg: string;
  activeBg: string;
  dot: string;
};

const ColorClasses: Record<string, ColorStyle> = {
  blue:   { border: "border-blue-400",   bg: "bg-blue-500",   activeBg: "bg-blue-100",   dot: "bg-blue-500"   },
  green:  { border: "border-green-400",  bg: "bg-green-500",  activeBg: "bg-green-100",  dot: "bg-green-500"  },
  red:    { border: "border-red-400",    bg: "bg-red-500",    activeBg: "bg-red-100",    dot: "bg-red-500"    },
  yellow: { border: "border-yellow-400", bg: "bg-yellow-500", activeBg: "bg-yellow-100", dot: "bg-yellow-500" },
  cyan:   { border: "border-cyan-400",   bg: "bg-cyan-500",   activeBg: "bg-cyan-100",   dot: "bg-cyan-500"   }
};

const Fallback: ColorStyle = {
  border: "border-gray-400",
  bg: "bg-gray-500",
  activeBg: "bg-gray-100",
  dot: "bg-gray-500",
};

const DynamicButtonFilters: React.FC<Props> = ({
  data = {},
  onFilterChange,
  activeFilter = null,
}) => {
  const [loading, setLoading] = useState(true);
  const [listFilters, setListFilters] = useState<FilterItem[]>([]);

  const fetchCounterData = async () => {
    setLoading(true);
    const resData = await sessionStorage.getItem("worklistTATFilters");
    if (resData) {
      setListFilters(JSON.parse(resData));
    } else {
      const { data: resData, status } = await getWorklistTATFilters();
      if (status) {
        setListFilters((resData as any)?.data ?? []);
        sessionStorage.setItem("worklistTATFilters", JSON.stringify((resData as any)?.data ?? []));
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    localStorage.setItem("statusActiveKey", String(activeFilter));
  }, [activeFilter]);

  useEffect(() => {
    void fetchCounterData();
  }, []);

  const handleClick = (key: string) => {
    onFilterChange?.(key);
  };

  const visibleFilters = listFilters.filter((item) => {
    const colorKey = item.ColorName.toLowerCase();
    if (colorKey === "white") return false;
    const count = data[item.ColorName] ?? data[colorKey] ?? 0;
    return count > 0;
  });

  if (loading || visibleFilters.length === 0) return null;

  return (
    <div className="bg-gray-100 rounded-lg p-2 mb-2">
      <div className="flex gap-2 flex-wrap items-center">
        {visibleFilters.map((item) => {
          const colorKey = item.ColorName.toLowerCase();
          const colors: ColorStyle = ColorClasses[colorKey] ?? Fallback;
          const isActive = activeFilter === item.ColorName;
          const count = data[item.ColorName] ?? data[colorKey] ?? 0;

          return (
            <div
              key={item.ColorName}
              onClick={() => handleClick(item.ColorName)}
              className={`
                flex items-center justify-between gap-3
                rounded-2xl px-3 py-2 cursor-pointer select-none
                shadow-md border-l-4 transition-colors duration-150
                ${colors.border}
                ${isActive ? colors.activeBg : "bg-white"}
              `}
            >
              <div className="flex flex-col min-w-0">
                <span className="text-xs text-gray-700 font-medium capitalize leading-tight">
                  {item.Description}
                </span>
                {isActive && (
                  <span className="text-[10px] text-gray-400 leading-tight">
                    click to clear
                  </span>
                )}
              </div>

              <div
                className={`
                  w-9 h-9 rounded-full text-white flex items-center justify-center
                  font-bold text-sm shrink-0 shadow
                  ${colors.dot}
                `}
              >
                {count}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DynamicButtonFilters;